import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router"
import useSWR from "swr"
import { NetworkApi } from "../../apis"
import { ProductDetail } from "../../components/buyer-sales/product-detail"
import { flattenCatalogue, useCatalogue } from "../../components/buyer-sales/product-list/catalogue-data"
import { TableFrame } from "../../components/delivery/atoms"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DeniedCard, ListState } from "../../components/ui"

/**
 * Product page. Opened from the catalogue it gets the row in router state; opened
 * from a link (or after a reload) it loads the supplier's catalogue — the same
 * request and cache key the catalogue uses — and finds the variant there (B11).
 * The catalogue puts the supplier in `?supplier=` so only that one is loaded.
 */
export function BuyerSalesProductDetail() {
  const location = useLocation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [params] = useSearchParams()
  const { t } = useTranslation(["order", "common"])
  const ORD_LIST = usePermission("ORD_LIST")

  const stateProduct = (location.state as any)?.product
  const supplierParam = params.get("supplier") || stateProduct?.supplierBusinessId || null

  // Same key as the catalogue page's supplier list, so this is usually a cache hit.
  const { data: suppliers, isLoading: suppliersLoading } = useSWR(
    ORD_LIST.hasAccess ? `/ord/network/select/all-suppliers` : null,
    async () => {
      try {
        return (await NetworkApi.select({ regNumber: "", partnerName: "", limit: 1000 })) || []
      } catch {
        return []
      }
    },
  )

  const lookup = stateProduct
    ? []
    : (suppliers || []).filter((s: any) => !supplierParam || s.id === supplierParam)
  const { data: catalogue, isLoading: catalogueLoading, error, mutate } = useCatalogue(lookup)
  const product = stateProduct || flattenCatalogue(catalogue).find((p) => p.id === id)
  const supplier = (suppliers || []).find((s: any) => s.id === (product?.supplierBusinessId || supplierParam)) || null

  const breadcrumb = [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/buyer-sales/new", label: t("order:catalog.title") },
    { label: t("order:buyerSales.detail") },
  ]

  if (!ORD_LIST.hasAccess) {
    return (
      <PageLayout title={t("order:catalog.title")} breadcrumb={breadcrumb} noFrame>
        <DeniedCard permission="ORD_LIST" />
      </PageLayout>
    )
  }

  if (!product) {
    const loading = suppliersLoading || catalogueLoading
    return (
      <PageLayout title={loading ? "" : t("order:catalog.detail.notFound")} breadcrumb={breadcrumb} noFrame>
        <TableFrame>
          {loading ? (
            <ListState state="loading" />
          ) : error ? (
            <ListState state="error" onRetry={() => mutate()} />
          ) : (
            <ListState
              state="empty"
              title={t("order:catalog.detail.notFound")}
              description={t("order:catalog.detail.notFoundHint")}
              action={{ label: t("order:catalog.detail.backToCatalog"), onClick: () => navigate("/buyer-sales/new") }}
            />
          )}
        </TableFrame>
      </PageLayout>
    )
  }

  return (
    <ProductDetail
      product={product}
      supplier={supplier}
      isMultiSupplier={(location.state as any)?.isMultiSupplier ?? (suppliers || []).length > 1}
    />
  )
}
